import React, { useEffect, useState } from 'react';
import { Info } from 'lucide-react';

interface InsulinSyringeChartProps {
  syringeCapacity?: number;
  syringeUnits?: number;
  onUnitSelect?: (units: number) => void;
}

export const InsulinSyringeChart: React.FC<InsulinSyringeChartProps> = ({
  syringeCapacity = 100, // Default to 100 ml
  syringeUnits = 100,
  onUnitSelect
}) => {
  const maxUnits = syringeCapacity;

  useEffect(() => {
    setCurrentUnits((prevUnits) => Math.min(prevUnits, syringeCapacity)); // Clamp to capacity
  }, [syringeCapacity, syringeUnits]);

  const [dragging, setDragging] = useState(false);
  const [currentUnits, setCurrentUnits] = useState(syringeUnits);

  const syringeWidth = 300;
  const syringeHeight = 60;
  const markingGap = (syringeWidth - 40) / maxUnits;

  const handleStart = (event: React.MouseEvent<SVGSVGElement> | React.TouchEvent<SVGSVGElement>) => {
    setDragging(true);
    handleDrag(event);
  };

  const handleMove = (event: React.MouseEvent<SVGSVGElement> | React.TouchEvent<SVGSVGElement>) => {
    if (!dragging) return;
    handleDrag(event);
  };

  const handleEnd = () => {
    setDragging(false);
    if (onUnitSelect) {
      onUnitSelect(currentUnits);
    }
  };

  const handleDrag = (event: React.MouseEvent<SVGSVGElement> | React.TouchEvent<SVGSVGElement>) => {
    const rect = (event.currentTarget as SVGSVGElement).getBoundingClientRect();
    const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX;
    const x = clientX - rect.left;
    const units = Math.round((x - 20) / markingGap);
    const clampedUnits = Math.max(0, Math.min(maxUnits, units));
    setCurrentUnits(clampedUnits);
    if (onUnitSelect) {
      onUnitSelect(clampedUnits);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = parseInt(event.target.value, 10);
    const clampedValue = Math.max(0, Math.min(maxUnits, isNaN(inputValue) ? 0 : inputValue));
    setCurrentUnits(clampedValue);
    if (onUnitSelect) {
      onUnitSelect(clampedValue);
    }
  };

  const plungerHeight = 70;
  const plungerWidth = 10;

  const needleBaseHeight = 20;

  return (
    <div className="relative flex flex-col items-center">
      <svg
        width={syringeWidth + 10}
        height={syringeHeight + 80}
        onMouseDown={handleStart}
        onMouseMove={handleMove}
        onMouseUp={handleEnd}
        onMouseLeave={handleEnd}
        onTouchStart={handleStart}
        onTouchMove={handleMove}
        onTouchEnd={handleEnd}
        className="cursor-pointer"
        style={{ userSelect: 'none' }}
      >
        {/* Syringe Needle Base */}
        <rect
          x={15}
          y={syringeHeight - needleBaseHeight / 2}
          width={3}
          height={needleBaseHeight}
          fill="#334155"
          stroke="#334155"
          strokeWidth="2"
        />

        {/* Syringe Plunger */}
        <rect
          x={280}
          y={syringeHeight - plungerHeight / 2}
          width={plungerWidth}
          height={plungerHeight}
          fill="grey"
          stroke="#334155"
          strokeWidth="2"
        />
        <rect
          x={290}
          y={syringeHeight - plungerHeight / 6}
          width={plungerWidth}
          height={plungerHeight / 3}
          fill="grey"
          stroke="#334155"
          strokeWidth="2"
        />

        {/* Syringe Body */}
        <rect
          x={20}
          y={30}
          width={syringeWidth - 40}
          height={syringeHeight}
          fill="white"
          stroke="#334155"
          strokeWidth="2"
        />

        {/* Unit Markings */}
        {Array.from({ length: maxUnits + 1 }).map((_, i) => (
          <g key={i}>
            <line
              x1={20 + i * markingGap}
              y1={30 + syringeHeight}
              x2={20 + i * markingGap}
              y2={i % 10 === 0 ? 30 + syringeHeight + 15 : 30 + syringeHeight + 10}
              stroke="#334155"
              strokeWidth={i % 10 === 0 ? 2 : 1}
            />
            {i % 10 === 0 && (
              <text
                x={20 + i * markingGap}
                y={30 + syringeHeight + 30}
                textAnchor="middle"
                className="text-xs fill-slate-700"
              >
                {i}
              </text>
            )}
          </g>
        ))}

        {/* Filled Amount */}
        <rect
          x={20}
          y={31}
          width={currentUnits * markingGap}
          height={syringeHeight - 2}
          fill="#3b82f6"
          fillOpacity="0.3"
        />

        {/* Plunger */}
        <rect
          x={20 + currentUnits * markingGap - 2}
          y={25}
          width={8}
          height={syringeHeight + 10}
          fill="#334155"
        />
        <circle
          cx={20 + currentUnits * markingGap + 2}
          cy={20}
          r={10}
          fill="#334155"
        />

        {/* Needle */}
        <line
          x1={0}
          y1={30 + syringeHeight / 2}
          x2={20}
          y2={30 + syringeHeight / 2}
          stroke="#334155"
          strokeWidth="2"
        />
      </svg>

      {/* Selected Units Input */}
      <div className="mt-4 flex items-center gap-2">
        <Info className="w-5 h-5 text-blue-500" />
        <input
          type="number"
          className="w-16 p-1 text-center border rounded-md"
          value={currentUnits}
          onChange={handleInputChange}
          min={0}
          max={maxUnits}
        />
        <span className="text-lg font-semibold">Units</span>
      </div>
    </div>
  );
};
